import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { CheckBox } from '@components/core-ui/form-elements/CheckBox';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useElements, useStripe } from '@stripe/react-stripe-js';
import type { Stripe, StripeCardNumberElement } from '@stripe/stripe-js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PaymentService } from 'app/API';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { useCallback, useRef, useState } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { TProps } from '../../types/TProps';
import { getInputOptions } from './helpers/inputOptions';
import AmexIcon from '@components/icons/PaymentAmex.svg';
import DiscoverIcon from '@components/icons/PaymentDiscover.svg';
import MastercardIcon from '@components/icons/PaymentMastercard.svg';
import VisaIcon from '@components/icons/PaymentVisa.svg';
import { StyledCardNumberElement } from './styles/StyledCardNumberElement';
import { StyledCVCElement } from './styles/StyledCVCElement';
import { StyledExpiredElement } from './styles/StyledExpiredElement';
import { StyledSpinner } from './styles/StyledSpinner';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { Content } from './styles/Content';
import { StripeChangeEvent } from './types/StripeChangeEvent';
import { ModalTitle } from '@modals/core-ui/ModalTitle';

export const Form = ({ onCancel, onSuccess, shouldShowSaveMethodCheckbox, billingAccountId }: TProps) => {
  const { call } = useAPI();
  const { organization } = useAuthContext();
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const requestErrorMessageRef = useRef<string | undefined>(undefined);

  const [isAgree, setIsAgree] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [savePaymentMethod, setSavePaymentMethod] = useState(!shouldShowSaveMethodCheckbox);
  const [focusedElement, setFocusedElement] = useState('');
  const [stripeError, setStripeError] = useState({
    cardNumber: '',
    cardCvc: '',
    cardExpiry: '',
  });
  const [cardFieldsComplete, setCardFieldsComplete] = useState({
    cardNumber: false,
    cardCvc: false,
    cardExpiry: false,
  });
  const inputOptions = getInputOptions();

  const validate = useCallback((e: StripeChangeEvent) => {
    setStripeError((prev) => ({ ...prev, [e.elementType]: e?.error?.message ?? '' }));
    setCardFieldsComplete((prev) => ({ ...prev, [e.elementType]: e.complete && !e.error }));
  }, []);

  const isCardDetailsComplete =
    cardFieldsComplete.cardNumber && cardFieldsComplete.cardCvc && cardFieldsComplete.cardExpiry;

  const showErrorAndCancel = (error: string) => {
    toast.error(error, {
      duration: 5000,
    });
    setTimeout(() => {
      onCancel();
    }, 5000);
  };

  const addPaymentMethodMutation = useMutation({
    mutationFn: async ({ stripe, cardNumber }: { stripe: Stripe; cardNumber: StripeCardNumberElement }) => {
      requestErrorMessageRef.current = undefined;
      const paymentMethod = await stripe.createPaymentMethod({
        type: 'card',
        card: cardNumber,
      });
      if (!paymentMethod.paymentMethod) {
        return { status: 'notCreated' } as const;
      }
      const paymentMethodId = paymentMethod.paymentMethod.id;
      if (!savePaymentMethod) {
        return { status: 'created', paymentMethodId } as const;
      }
      const baId = billingAccountId ?? organization?.billingAccount.id ?? '';
      const response = await call(
        () =>
          PaymentService.billingAccountAddPaymentMethod({
            baId,
            requestBody: { paymentMethodType: 'card', paymentMethodId },
          }),
        {
          onError: (message) => {
            requestErrorMessageRef.current = message;
          },
        },
      );
      return { status: 'saved', paymentMethodId, baId, response } as const;
    },
    onSuccess: (result) => {
      if (result.status === 'notCreated') {
        showErrorAndCancel('Something went wrong, please try again later');
        return;
      }
      if (result.status === 'saved') {
        if (!result.response.success) {
          showErrorAndCancel(result.response.error || 'Something went wrong, please try again later');
          return;
        }
        void queryClient.invalidateQueries({ queryKey: billingKeys.paymentMethodsByAccount(result.baId) });
      }
      onSuccess(result.paymentMethodId);
    },
    onError: (error) => {
      console.error(error);
      showErrorAndCancel(requestErrorMessageRef.current ?? 'Something went wrong, please try again later');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || addPaymentMethodMutation.isPending || !isAgree || !isCardDetailsComplete) {
      return;
    }

    const cardNumber = elements.getElement('cardNumber');
    if (!cardNumber) {
      return;
    }

    setIsSubmitted(true);
    addPaymentMethodMutation.mutate({ stripe, cardNumber });
  };

  const handleChangeCheckbox = () => {
    setSavePaymentMethod(!savePaymentMethod);
  };

  return (
    <ModalWrapper width="380px">
      <ModalTitle>Enter Payment Details</ModalTitle>
      <Content>
        <form className="payment-form" onSubmit={handleSubmit}>
          <div>
            <StyledCardNumberElement
              onChange={validate}
              onFocus={() => setFocusedElement('cardNumber')}
              onBlur={() => setFocusedElement('')}
              focused={focusedElement === 'cardNumber'}
              options={{ placeholder: 'Card Number', ...inputOptions }}
              error={!!stripeError.cardNumber}
              id="cardNumber"
              className="card-number-input"
            />
            <div className="flex50percents">
              <StyledExpiredElement
                onChange={validate}
                focused={focusedElement === 'cardExpiry'}
                onFocus={() => setFocusedElement('cardExpiry')}
                onBlur={() => setFocusedElement('')}
                options={{ placeholder: 'Expiry date', ...inputOptions }}
                error={!!stripeError.cardExpiry}
                id="cardExpiry"
                className="card-expiry-input"
              />

              <StyledCVCElement
                onChange={validate}
                focused={focusedElement === 'cardCvc'}
                onFocus={() => setFocusedElement('cardCvc')}
                onBlur={() => setFocusedElement('')}
                options={{ placeholder: 'CVC', ...inputOptions }}
                error={!!stripeError.cardCvc}
                id="cardCvc"
                className="card-cvc-input"
              />
            </div>

            <div className="checkBoxWrapper">
              <CheckBox
                name="accept-terms"
                checked={isAgree}
                onChange={() => setIsAgree(!isAgree)}
                testId="accept-terms-checkbox"
              />
              I agree to the{' '}
              <a href="https://voicescript.ai/terms-of-service/" target="_blank" rel="noreferrer">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="https://voicescript.ai/privacy-policy/" target="_blank" rel="noreferrer">
                Privacy Policy
              </a>
            </div>

            {shouldShowSaveMethodCheckbox ? (
              <div className="checkBoxWrapper">
                <CheckBox
                  name="save-payment-method"
                  checked={savePaymentMethod}
                  onChange={() => handleChangeCheckbox()}
                />
                Save this method
              </div>
            ) : null}
          </div>

          <div className="iconsWrapper">
            <AmexIcon aria-hidden="true" focusable={false} />
            <MastercardIcon aria-hidden="true" focusable={false} />
            <VisaIcon aria-hidden="true" focusable={false} />
            <DiscoverIcon aria-hidden="true" focusable={false} />
          </div>
          <div className="buttonWrapper">
            <OutlinedButton className="w-[140px]" onClick={onCancel}>
              Cancel
            </OutlinedButton>

            <Button
              className="w-[140px]"
              data-testid="submitButton"
              type="submit"
              disabled={
                !isAgree ||
                isSubmitted ||
                !!stripeError.cardNumber ||
                !!stripeError.cardCvc ||
                !!stripeError.cardExpiry ||
                !isCardDetailsComplete
              }
            >
              {isSubmitted ? <StyledSpinner /> : 'Submit'}
            </Button>
          </div>
        </form>
        <Toaster position="bottom-right" reverseOrder={false} />
      </Content>
    </ModalWrapper>
  );
};
